import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, BRL, LOGO_URL } from "@/lib/api";
import { STATUS_FLOW, STATUS_META } from "@/lib/status";
import { Check, Loader2 } from "lucide-react";
import OrderPushButton from "@/components/OrderPushButton";

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/orders/${id}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  if (notFound)
    return (
      <Wrap>
        <p className="text-center py-20 text-muted-foreground">Pedido não encontrado.</p>
      </Wrap>
    );

  if (!order)
    return (
      <Wrap>
        <div className="flex items-center justify-center py-20 text-muted-foreground gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Carregando...
        </div>
      </Wrap>
    );

  const cancelled = order.status === "cancelado";
  const currentIdx = STATUS_FLOW.indexOf(order.status);

  return (
    <Wrap>
      <div className="animate-fade-up">
        <p className="uppercase tracking-[0.25em] text-primary text-xs font-bold">Acompanhe</p>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">
          Pedido #{order.order_number}
        </h1>
        <p className="text-muted-foreground mt-1">Olá, {order.customer_name}! Este é o status do seu pedido.</p>

        <OrderPushButton orderId={order.id} finished={cancelled || order.status === "entregue"} />

        {cancelled ? (
          <div className="mt-8 rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-center">
            <p className="font-display font-bold text-xl text-destructive">Pedido cancelado</p>
          </div>
        ) : (
          <div className="mt-8 space-y-1" data-testid="order-timeline">
            {STATUS_FLOW.map((s, i) => {
              const done = i < currentIdx;
              const current = i === currentIdx;
              const meta = STATUS_META[s];
              return (
                <div key={s} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div
                      className="w-9 h-9 rounded-full grid place-items-center border-2 transition-colors"
                      style={{
                        background: done || current ? meta.color : "transparent",
                        borderColor: done || current ? meta.color : "hsl(var(--border))",
                        color: "#fff",
                      }}
                    >
                      {done ? <Check className="w-4 h-4" /> : current ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    </div>
                    {i < STATUS_FLOW.length - 1 && (
                      <div
                        className="w-0.5 flex-1 min-h-[28px]"
                        style={{ background: done ? meta.color : "hsl(var(--border))" }}
                      />
                    )}
                  </div>
                  <div className={`pb-5 ${current ? "" : done ? "opacity-90" : "opacity-45"}`}>
                    <p className="font-semibold">{meta.label}</p>
                    <p className="text-sm text-muted-foreground">{meta.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-border bg-card p-5">
          <p className="font-display font-bold text-lg mb-3">Itens</p>
          <div className="space-y-2">
            {order.items.map((it, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span>{it.qty}x {it.name}</span>
                <span className="text-muted-foreground">{BRL(it.price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-3 pt-3 space-y-1 text-sm">
            {order.delivery_fee != null && (
              <>
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span>{BRL(order.subtotal ?? order.total)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{order.fulfillment_type === "retirada" ? "Retirada" : "Taxa de entrega"}</span>
                  <span>{order.fulfillment_type === "retirada" ? "Grátis" : BRL(order.delivery_fee)}</span>
                </div>
                {order.card_fee > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Taxa do cartão{order.card_fee_percent ? ` (${order.card_fee_percent}%)` : ""}</span>
                    <span>{BRL(order.card_fee)}</span>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between font-bold text-base pt-1">
              <span>Total</span>
              <span className="text-primary">{BRL(order.total)}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">Pagamento: {order.payment_method}</p>
          <p className="text-sm text-muted-foreground">
            {order.fulfillment_type === "retirada"
              ? "Retirada no balcão"
              : `Entrega: ${order.address}${order.neighborhood ? ` (${order.neighborhood})` : ""}`}
          </p>
        </div>

        <a href="/" className="inline-flex mt-6 rounded-full border border-border px-5 py-2.5 font-medium hover:bg-muted transition-colors">
          Fazer novo pedido
        </a>
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center">
          <a href="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Paulista's Lanches & Pizzas" className="h-11 w-auto object-contain" />
            <span className="font-display font-black text-lg tracking-tight hidden sm:inline">Paulista's Lanches &amp; Pizzas</span>
          </a>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8">{children}</main>
    </div>
  );
}
